/**
 * @module UseContainerTriageSelection
 * @purpose Selection state for the triage panel: per-item and per-group
 *   toggling, with already-ingested items gated behind an explicit
 *   re-ingest confirmation (bulk-op confirmation working agreement).
 * @depends container-triage.helpers (isAlreadyIngested)
 * @dependedOnBy use-container-triage
 *
 * @rules
 * - State: local useState Sets, reset whenever the caller supplies a new
 *   `resetToken` (a fresh inventory load)
 * - Errors: n/a -- pure client-side selection bookkeeping
 */

import { useCallback, useState } from 'react'
import { isAlreadyIngested } from './container-triage.helpers'
import type { ContainerInventoryItem, InventoryGroup } from '../../../types/evidence-containers'

export interface UseContainerTriageSelectionResult {
  selectedKeys: ReadonlySet<string>
  reingestKeys: ReadonlySet<string>
  pendingReingestItem: ContainerInventoryItem | null
  toggleItem: (item: ContainerInventoryItem) => void
  toggleGroup: (group: InventoryGroup) => void
  confirmReingest: () => void
  cancelReingest: () => void
  reset: () => void
}

function withoutKey(keys: ReadonlySet<string>, key: string): Set<string> {
  const next = new Set(keys)
  next.delete(key)
  return next
}

export function useContainerTriageSelection(): UseContainerTriageSelectionResult {
  const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<string>>(new Set())
  const [reingestKeys, setReingestKeys] = useState<ReadonlySet<string>>(new Set())
  const [pendingReingestItem, setPendingReingestItem] = useState<ContainerInventoryItem | null>(null)

  const deselect = useCallback((item: ContainerInventoryItem) => {
    setSelectedKeys((prev) => withoutKey(prev, item.itemKey))
    setReingestKeys((prev) => withoutKey(prev, item.itemKey))
  }, [])

  const toggleItem = useCallback(
    (item: ContainerInventoryItem) => {
      if (selectedKeys.has(item.itemKey)) {
        deselect(item)
        return
      }
      if (isAlreadyIngested(item)) {
        setPendingReingestItem(item)
        return
      }
      setSelectedKeys((prev) => new Set(prev).add(item.itemKey))
    },
    [selectedKeys, deselect]
  )

  const toggleGroup = useCallback(
    (group: InventoryGroup) => {
      const selectable = group.items.filter((item) => !isAlreadyIngested(item))
      const allSelected = selectable.every((item) => selectedKeys.has(item.itemKey))
      setSelectedKeys((prev) => {
        const next = new Set(prev)
        for (const item of selectable) {
          if (allSelected) next.delete(item.itemKey)
          else next.add(item.itemKey)
        }
        return next
      })
    },
    [selectedKeys]
  )

  const confirmReingest = useCallback(() => {
    if (!pendingReingestItem) return
    const key = pendingReingestItem.itemKey
    setSelectedKeys((prev) => new Set(prev).add(key))
    setReingestKeys((prev) => new Set(prev).add(key))
    setPendingReingestItem(null)
  }, [pendingReingestItem])

  const cancelReingest = useCallback(() => {
    setPendingReingestItem(null)
  }, [])

  const reset = useCallback(() => {
    setSelectedKeys(new Set())
    setReingestKeys(new Set())
    setPendingReingestItem(null)
  }, [])

  return {
    selectedKeys,
    reingestKeys,
    pendingReingestItem,
    toggleItem,
    toggleGroup,
    confirmReingest,
    cancelReingest,
    reset,
  }
}
